import type { CustomerPurchaseDay } from '@order-pilot/shared';
import { agingText } from '@/features/portal/aging';
import { formatCount, formatMoney, formatPosDate, formatPosTime } from '@/lib/format';

/**
 * A customer's bills grouped under the day they were placed: a heading with the
 * date, how long ago that was, and the day's total, then that day's bills.
 *
 * Reading down the headings shows how often they come — a run of days a week
 * apart is a regular; a gap of months before the latest one is a lapsed
 * customer coming back.
 */
export function PurchaseDays({
  days,
  currency,
}: {
  days: readonly CustomerPurchaseDay[];
  currency: string;
}) {
  return (
    <div className="divide-y divide-slate-200">
      {days.map((day) => (
        <section key={day.date} aria-label={`Bills on ${formatPosDate(day.date)}`}>
          <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 bg-slate-50 px-5 py-2.5">
            <h3 className="text-sm font-semibold text-slate-900">
              {formatPosDate(day.date)}
              <span className="ml-2 font-normal text-slate-500">{agingText(day.daysAgo)}</span>
            </h3>
            <p className="text-xs tabular-nums text-slate-500">
              {formatCount(day.bills)} bill{day.bills === 1 ? '' : 's'} · {formatMoney(day.total, currency)}
            </p>
          </header>
          <ul className="divide-y divide-slate-100">
            {day.purchases.map((p) => (
              <li
                key={p.orderNumber}
                className="grid grid-cols-2 items-center gap-x-4 gap-y-1 px-5 py-3 text-sm sm:grid-cols-[1.2fr_1fr_0.8fr_1fr_1fr_1fr]"
              >
                <div>
                  <span className="font-medium text-slate-900">{p.orderNumber}</span>
                  <p className="text-xs text-slate-400">{formatPosTime(p.time)}</p>
                </div>
                <span className="text-slate-600">{p.branch || '—'}</span>
                <span className="text-slate-600 sm:text-right">
                  {formatCount(p.items)} item{p.items === 1 ? '' : 's'}
                </span>
                <span className="font-medium tabular-nums text-slate-900 sm:text-right">
                  {formatMoney(p.total, currency)}
                </span>
                <span className="text-slate-600">{p.paymentMethod || '—'}</span>
                <span className={p.paymentStatus === 'Unpaid' ? 'text-amber-700' : 'text-slate-600'}>
                  {p.paymentStatus}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
